/**
 * The image-carousel module: the URL helpers and the renderability contract the card surfaces read.
 *
 * <p><b>The `ImageCarouselDisplayer` COMPONENT was deleted on 2026-08-24</b> (wave-13 work list item 33,
 * the appendix entry). It had no production render site anywhere in `src` — the only JSX use in the whole
 * package was its own renderability test, and every other reference to this module is one of the four
 * below. Two unkeyed English literals lived on it as prop defaults (`brokenImageTitle`,
 * `brokenImageDescription`), which is a rule-11 and rule-12 defect that neither localization build guard
 * can see: key parity and the verbatim check read CATALOGS, and a literal that never reaches one cannot
 * fail either.
 *
 * <p>What survives, and who reads it:
 * <ul>
 *   <li>`ImageCarouselRenderabilityState` — `DiscussionCardDisplayer`, `DiscussionCardMediaCarousel`,
 *       `SeriesCardDisplayer` (type-only imports);</li>
 *   <li>`buildOrderedImageUrls` — `SeriesImageUtils`;</li>
 *   <li>`parseImageUrls` / `isMeaningfulImageUrl` — the helpers the above are built from, pinned by
 *       `ImageCarouselDisplayer.test.ts`.</li>
 * </ul>
 *
 * <p>The module keeps its name and its path deliberately: it is still the home of the carousel
 * renderability contract those three card surfaces read, and renaming it would move four importers for no
 * behavioural gain. If a carousel component is ever needed again, build it against
 * `components/ui/carousel` rather than restoring this one — and give its arrow labels real catalog keys
 * (`pagination.previous` / `pagination.next` exist in all 25 catalogs).
 */
import {deduplicateImageUrlsPreservingBestVariant} from "@/main/util/ImageUrlHelper";

export type ImageCarouselRenderabilityState = {
	totalImages: number;
	loadedImages: number;
	failedImages: number;
	hasRenderableImages: boolean;
	allImagesFailed: boolean;
	isResolving: boolean;
};

// treat null-like strings and obviously-broken urls as non-URLs
export function isMeaningfulImageUrl(raw?: unknown): boolean {
	if (raw == null) return false;
	const s = String(raw).trim();
	if (!s) return false;
	const lower = s.toLowerCase();

	return lower !== "null"
		&& lower !== '"null"'
		&& lower !== "undefined"
		&& lower !== '"undefined"'
		&& !/\/(?:null|undefined)(?:$|[?#\/])/i.test(s);
}

export function parseImageUrls(raw?: unknown): string[] {
	if (!isMeaningfulImageUrl(raw)) return [];

	if (Array.isArray(raw)) {
		return raw.map(String).map((item) => item.trim()).filter(isMeaningfulImageUrl);
	}

	if (typeof raw !== "string") {
		return [String(raw)].map((item) => item.trim()).filter(isMeaningfulImageUrl);
	}

	const value = raw.trim();
	if (!isMeaningfulImageUrl(value)) return [];

	if ((value.startsWith("[") && value.endsWith("]")) || (value.startsWith('"') && value.endsWith('"'))) {
		try {
			const parsed = JSON.parse(value);
			if (Array.isArray(parsed)) {
				return parsed.map(String).map((item) => item.trim()).filter(isMeaningfulImageUrl);
			}
			if (typeof parsed === "string") {
				return [parsed.trim()].filter(isMeaningfulImageUrl);
			}
		} catch {
			// fall through to the comma-separated parser
		}
	}

	return value
		.replace(/^\[|]$/g, "")
		.split(",")
		.map((item) => item.trim().replace(/^"|"$/g, ""))
		.filter(isMeaningfulImageUrl);
}

export function buildOrderedImageUrls(input: {
	thumbnailUrl?: unknown;
	imageUrls?: unknown;
}): string[] {
	const thumbnail = parseImageUrls(input.thumbnailUrl)[0];
	return deduplicateImageUrlsPreservingBestVariant([
		...(thumbnail ? [thumbnail] : []),
		...parseImageUrls(input.imageUrls),
	]);
}

